import LabelInput from 'components/common/forms/LabelInput';
import SingleBtn from 'components/common/elements/SingleBtn';
import { useState } from 'react';
import styles from 'styles/pages/common/FindPassword.module.scss';
import LoginLayout from 'components/common/layout/LoginLayout';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import { FormProvider, useForm } from 'react-hook-form';
import { FindPasswordFormDefaultValues } from 'constants/auth';
import { useMutation } from 'react-query';
import { isTCustomAxiosError } from 'apis/config/axios';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from 'constants/routePath';
import { useSetAtom } from 'jotai';
import { findPasswordUserInfoAtom } from 'store/auth';
import { UserRoleType } from 'types/apis/auth';
import {
  TVerificationEmailForms,
  VerificationEmailFormField,
} from 'types/form/auth';
import {
  postCheckVerificationNumber,
  postSendVerificationEmail,
} from 'apis/auth';
import useModal from 'hooks/useModal';

const FindPassword = () => {
  const { openModal } = useModal();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isVerificationNumberInputShow, setIsVerificationNumberInputShow] =
    useState(false);
  const [
    isVerificationNumberSubmitBtnShow,
    setIsVerificationNumberSubmitBtnShow,
  ] = useState(false);
  const [isSendVerificationEmail, setIsSendVerificationEmail] = useState(false);
  const [isRetry, setIsRetry] = useState(false);

  // 재발송 5회 초과 상태 추가
  const [retryExceed, setRetryExceed] = useState(false);

  const setFindPasswordUserInfo = useSetAtom(findPasswordUserInfoAtom);

  const navigate = useNavigate();

  const subtitle =
    '계정에 등록된 이메일 주소로 인증번호가 전송됩니다.\n인증번호로 로그인 후 반드시 비밀번호를 변경해 주세요.';

  const methods = useForm<TVerificationEmailForms>({
    defaultValues: FindPasswordFormDefaultValues,
  });
  const {
    formState: { errors: formErrors },
    handleSubmit,
  } = methods;

  const firstFormErrorKey = Object.keys(
    formErrors
  )[0] as VerificationEmailFormField;

  // 인증번호 전송
  const { mutate: postSendVerificationEmailMutation } = useMutation(
    postSendVerificationEmail,
    {
      onSuccess: () => {
        setIsVerificationNumberInputShow(true);
        setIsVerificationNumberSubmitBtnShow(true);
        setIsSendVerificationEmail(true);
        setIsRetry(false);
        setErrorMsg(null);
        openModal({
          title: '인증번호 전송',
          text: '계정에 등록한 이메일로 인증번호가 전송되었습니다. 인증번호로 재로그인해 주세요.',
        });
      },
      onError: (error) => {
        if (isTCustomAxiosError(error)) {
          setErrorMsg(error.message);
          setIsRetry(false);
          if (
            error.code === 400 &&
            error.message ===
              '[비관리자] 인증번호 전송 횟수가 5회를 초과하였습니다.'
          ) {
            setRetryExceed(true);
          } else if (error.code === 404) {
            setErrorMsg('등록된 계정이 없습니다. 다시 확인해 주세요.');
          }
        }
      },
    }
  );

  // 인증번호 확인
  const { mutate: postCheckVerificationNumberMutation } = useMutation(
    postCheckVerificationNumber,
    {
      onSuccess: (data) => {
        // data code가 200이지만 data가 false로 올 때는 인증번호 불일치로 봄
        if (!data) {
          setErrorMsg('인증번호가 일치하지 않습니다.');
        } else if (data.code === 404) {
          setErrorMsg('계정, 이름, 학번 / 교직원번호를 다시 확인해 주세요.');
        } else {
          const userInfo = methods.getValues();
          setFindPasswordUserInfo({
            userRole: userInfo.userRole,
            loginId: userInfo.loginId,
          });

          navigate(ROUTE_PATH.common.RESET_PASSWORD);
        }
      },
      onError: (error) => {
        if (isTCustomAxiosError(error)) {
          setErrorMsg(error.message);
          if (error.code === 404) {
            setErrorMsg('계정, 이름, 학번 / 교직원번호를 다시 확인해 주세요.');
          } else if (error.code !== 200 && retryExceed) {
            openModal({
              title: '문의하기',
              text: '[관리자] 인증번호 전송 횟수가 5회를 초과하였습니다. 카이아이 고객센터로 문의해 주세요.',
            });
          } else {
            setErrorMsg('인증번호가 일치하지 않습니다.');
          }
        }
      },
    }
  );

  const onValid = (data: TVerificationEmailForms) => {
    // const data = methods.getValues();

    // [인증번호 전송] 버튼 클릭 시
    if (!isSendVerificationEmail && !isRetry) {
      postSendVerificationEmailMutation({
        name: data.name,
        loginId: data.loginId,
        userRole: data.userRole,
      });
      // [확인] 버튼 클릭 시
    } else if (isSendVerificationEmail && !isRetry && !!data.verificationNum) {
      postCheckVerificationNumberMutation({
        name: data.name,
        loginId: data.loginId,
        userRole: data.userRole,
        verificationNum: data.verificationNum,
      });

      // [재전송] 버튼 클릭 시
    } else if (isSendVerificationEmail && isRetry) {
      openModal({
        title: '인증번호 전송',
        text: '계정에 등록한 이메일로 인증번호가 전송되었습니다. 인증번호로 재로그인해 주세요.',
      });
      postSendVerificationEmailMutation({
        name: data.name,
        loginId: data.loginId,
        userRole: data.userRole,
      });
    }
  };

  const onClickResend = () => {
    setIsRetry(true);
    postSendVerificationEmailMutation(methods.getValues());
  };

  return (
    <LoginLayout title="비밀번호 찾기" subtitle={subtitle}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onValid)}>
          <div className={styles.account_wrapper}>
            <label className={styles.label}>
              계정 선택 <span className={styles.span}>*</span>
            </label>
            <fieldset>
              <div className={styles.radio_wrapper}>
                <FormRadioBtn
                  value={UserRoleType.STUDENT}
                  labelText="학생"
                  name={VerificationEmailFormField.UserRole}
                  rules={{ required: '계정 선택은 필수입니다.' }}
                />
                <FormRadioBtn
                  value={UserRoleType.PROFESSOR}
                  labelText="교수 (교직원)"
                  name={VerificationEmailFormField.UserRole}
                  rules={{ required: '계정 선택은 필수입니다.' }}
                />
              </div>
            </fieldset>
          </div>
          <div className={styles.login_input}>
            <LabelInput
              labelText="이름"
              placeholderText="이름을 입력해 주세요."
              name={VerificationEmailFormField.Name}
              required={true}
              rules={{
                required: '이름을 입력해 주세요.',
                pattern: {
                  value: /^[a-zA-Z가-힣]{2,30}$/,
                  message:
                    '잘못된 입력값입니다. 입력한 정보를 다시 확인해 주세요.',
                },
              }}
            ></LabelInput>
            <LabelInput
              labelText="학번 / 교직원 번호"
              placeholderText="학번 또는 교직원 번호를 입력해 주세요."
              name={VerificationEmailFormField.LoginId}
              required={true}
              rules={{
                required: '학번 또는 교직원 번호를 입력해 주세요.',
                pattern: {
                  value: /^[a-zA-Z0-9]{4,12}$/,
                  message:
                    '잘못된 입력값입니다. 입력한 정보를 다시 확인해 주세요.',
                },
              }}
            ></LabelInput>
            {isVerificationNumberInputShow && (
              <div className="flex gap16">
                <LabelInput
                  labelText="인증번호"
                  placeholderText="인증번호를 입력해 주세요."
                  name={VerificationEmailFormField.VerificationNum}
                  required={true}
                  checkAvailability
                  btnText="재전송"
                  onBtnClick={onClickResend}
                  rules={{
                    required: '인증번호를 입력해 주세요.',
                  }}
                ></LabelInput>
              </div>
            )}
          </div>

          {errorMsg === null && Object.keys(formErrors).length === 0 && (
            <div className={styles.div}></div>
          )}
          {errorMsg && <span className={styles.error}>{errorMsg}</span>}
          {Object.keys(formErrors).length > 0 && (
            <span className={styles.error}>
              {formErrors[firstFormErrorKey]?.message}
            </span>
          )}

          <SingleBtn
            type="submit"
            btnText={
              !isVerificationNumberSubmitBtnShow ? '인증번호 전송' : '확인'
            }
            isBtnActive={true}
            btnStyle={{ width: '100%' }}
          />
        </form>
      </FormProvider>
      <div className={styles.caption_wrapper}>
        <span className={styles.caption}>
          등록된 정보가 기억이 나지 않는다면?
        </span>
        <button
          className={styles.caption_text}
          onClick={() =>
            openModal({
              title: '문의하기',
              text: '학과사무실 (051-200-3483)로 문의해 주세요.',
            })
          }
        >
          문의하기
        </button>
      </div>
    </LoginLayout>
  );
};

export default FindPassword;
